import React, { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, skipToken } from "@tanstack/react-query";
import { JobOfferPreviewDialog } from "@/features/job-offer/components/job-offer-preview-dialog";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useTRPC } from "@/trpc";
import { useOrganization } from "@/hooks/use-organization";
import { useOrgPath } from "@/hooks/useOrgPath";
import { Permission, useRolePermissions } from "@/hooks/use-role-permissions";
import {
  Users,
  Briefcase,
  MessageSquare,
  CheckCircle2,
  ArrowUpRight,
  Calendar,
  CalendarDays,
  MapPin,
  Sparkles,
  Building2,
  Clock,
  ShieldCheck,
} from "lucide-react";
import { format, formatDistanceToNow, parseISO } from "date-fns";
import { ProposalScheduleChanges } from "@/features/job-offer/components/proposal-schedule-changes";
import { OrgHomePushBanner } from "@/components/PushNotifications/OrgHomePushBanner";

export const Route = createFileRoute(
  "/_org/org/bu/$organizationBusinessUnitId/home/",
)({
  component: OrgDashboard,
});

function OrgDashboard() {
  const { user, savedOrg, savedOrgBusinessUnit } = Route.useRouteContext();
  const { selectedOrg, selectedOrgBusinessUnit } = useOrganization();
  const trpc = useTRPC();
  const orgPath = useOrgPath();
  const [selectedProposalJobOfferId, setSelectedProposalJobOfferId] = useState<
    string | null
  >(null);

  const activeBusinessUnitId =
    selectedOrgBusinessUnit?.id || savedOrgBusinessUnit?.id || undefined;
  const activeOrgName = selectedOrg?.name || savedOrg?.name;
  const activeBusinessUnitName =
    selectedOrgBusinessUnit?.name || savedOrgBusinessUnit?.name;

  const { hasPermission } = useRolePermissions(user);
  const canViewCredentials = hasPermission(Permission.ViewProviderCredentials);

  const {
    data: credentialStats,
    isLoading: credentialStatsLoading,
    isError: credentialStatsError,
    error: credentialStatsErrorData,
  } = useQuery(
    trpc.providers.getCredentialSummaryStats.queryOptions(
      canViewCredentials && activeBusinessUnitId
        ? {
            organizationBusinessUnitId: activeBusinessUnitId,
            providerPoolIds: [],
            affiliation: "hired",
          }
        : skipToken,
    ),
  );

  const {
    data: providers,
    isLoading: providersLoading,
    isError: providersError,
    error: providersErrorData,
  } = useQuery(
    trpc.profile.list.queryOptions(
      activeBusinessUnitId
        ? { organizationBusinessUnitId: activeBusinessUnitId }
        : {},
      {
        enabled: !!activeBusinessUnitId,
      },
    ),
  );

  const {
    data: jobPostsData,
    isLoading: jobPostsLoading,
    isError: jobPostsError,
    error: jobPostsErrorData,
  } = useQuery(
    trpc.jobPost.orgList.queryOptions(
      activeBusinessUnitId
        ? {
            organizationBusinessUnitId: activeBusinessUnitId,
            sortBy: "recentlyInterested",
          }
        : skipToken,
      {
        enabled: !!activeBusinessUnitId,
      },
    ),
  );

  // Extract jobs array from the paginated response
  const jobPosts = jobPostsData?.jobs;

  const {
    data: jobOffers,
    isLoading: jobOffersLoading,
    isError: jobOffersError,
    error: jobOffersErrorData,
  } = useQuery(
    trpc.jobOffer.organizationGetAll.queryOptions(
      activeBusinessUnitId
        ? {
            organizationBusinessUnitId: activeBusinessUnitId,
          }
        : skipToken,
      {
        enabled: !!activeBusinessUnitId,
      },
    ),
  );

  const { data: pendingProposals } = useQuery(
    trpc.jobOffer.orgListPendingProposals.queryOptions(
      {
        organizationBusinessUnitId: activeBusinessUnitId ?? "",
      },
      {
        enabled: !!activeBusinessUnitId,
      },
    ),
  );

  const { data: selectedProposalJobOffer } = useQuery({
    ...trpc.jobOffer.orgGet.queryOptions({
      id: selectedProposalJobOfferId ?? "",
    }),
    enabled: !!selectedProposalJobOfferId,
  });

  const { data: unreadMessages, isLoading: unreadMessagesLoading } = useQuery(
    trpc.chat.getUnreadMessagesCount.queryOptions(
      {
        organizationBusinessUnitId: activeBusinessUnitId,
      },
      {
        enabled: !!activeBusinessUnitId,
      },
    ),
  );

  const offerStatusCounts = useMemo(() => {
    const counts = {
      awaiting: 0,
      accepted: 0,
      declined: 0,
      canceled: 0,
    };

    (jobOffers ?? []).forEach((offer) => {
      if (offer.canceledByOrganizationBusinessUnitAt) {
        counts.canceled += 1;
        return;
      }
      const decisions = offer.decisions ?? [];
      const providerAccepted = decisions.some(
        (decision) => decision?.providerDecision === true,
      );
      if (providerAccepted) {
        counts.accepted += 1;
        return;
      }
      const providerDeclined = decisions.some(
        (decision) =>
          decision?.providerDecision === false || decision?.providerCanceledAt,
      );
      if (providerDeclined) {
        counts.declined += 1;
        return;
      }
      counts.awaiting += 1;
    });

    return counts;
  }, [jobOffers]);

  const totalSharedProviders = providers?.length ?? 0;
  const activeJobPostCount =
    jobPosts?.filter((job) => job.open && job.publishedAt)?.length ?? 0;
  const pendingReviewCount =
    jobPosts?.filter((job) => job.publishedAt && !job.reviewedAt)?.length ?? 0;
  const draftJobCount =
    jobPosts?.filter((job) => !job.publishedAt && job.open)?.length ?? 0;
  const totalInterestedCandidates =
    jobPosts?.reduce(
      (acc, job) => acc + (job.interestedCandidates?.length ?? 0),
      0,
    ) ?? 0;
  const totalUnreadMessages =
    unreadMessages?.reduce((acc, thread) => acc + thread.unreadCount, 0) ?? 0;

  const statsLoading =
    providersLoading ||
    jobPostsLoading ||
    jobOffersLoading ||
    unreadMessagesLoading;

  const highlightedJobPosts = useMemo(() => {
    return (jobPosts ?? []).slice(0, 4);
  }, [jobPosts]);

  const highlightedOffers = useMemo(() => {
    return (jobOffers ?? []).slice(0, 3);
  }, [jobOffers]);

  const highlightedProviders = useMemo(() => {
    return (providers ?? []).slice(0, 5);
  }, [providers]);

  const stats = useMemo(
    () => [
      {
        label: "Shared Providers",
        value: totalSharedProviders,
        description: "Profiles connected to this business unit",
        icon: Users,
      },
      {
        label: "Active Job Posts",
        value: activeJobPostCount,
        description: "Published roles currently visible to providers",
        icon: Briefcase,
      },
      {
        label: "Awaiting Offer Decisions",
        value: offerStatusCounts.awaiting,
        description: "Offers that still need provider responses",
        icon: CheckCircle2,
      },
      {
        label: "Unread Conversations",
        value: totalUnreadMessages,
        description: "Messages awaiting your team",
        icon: MessageSquare,
      },
    ],
    [
      totalSharedProviders,
      activeJobPostCount,
      offerStatusCounts.awaiting,
      totalUnreadMessages,
    ],
  );

  const hasBusinessUnit = Boolean(activeBusinessUnitId);

  return (
    <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10 space-y-6">
      <Card className="border border-slate-200 shadow-sm rounded-2xl">
        <CardContent className="p-6 sm:p-8">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
            <div className="space-y-3">
              {activeOrgName && (
                <p className="text-xs font-semibold tracking-wide uppercase text-slate-500">
                  {activeOrgName}
                  {activeBusinessUnitName ? ` • ${activeBusinessUnitName}` : ""}
                </p>
              )}
              <h1 className="text-3xl sm:text-4xl font-bold text-slate-900">
                Welcome back{user?.first_name ? `, ${user.first_name}` : ""}
              </h1>
              <p className="text-slate-600 max-w-2xl text-sm sm:text-base">
                Keep an eye on your staffing pipeline, review provider interest,
                and follow up on offers in one place.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
              <Button asChild className="w-full font-semibold sm:w-auto">
                <Link to={orgPath("/jobs/post/create")}>
                  <Sparkles className="mr-2 h-4 w-4" />
                  Create Job Post
                </Link>
              </Button>
              <Button asChild variant="outline" className="w-full sm:w-auto">
                <Link to={orgPath("/providers/all")}>
                  <Users className="mr-2 h-4 w-4" />
                  View Providers
                </Link>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <OrgHomePushBanner />

      {pendingProposals && pendingProposals.length > 0 && (
        <Card className="border border-amber-200 shadow-sm rounded-2xl">
          <CardHeader className="space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <CalendarDays className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <CardTitle className="text-xl font-semibold text-slate-900">
                    Pending Proposals
                  </CardTitle>
                  <CardDescription>
                    Schedule change proposals awaiting review.
                  </CardDescription>
                </div>
              </div>
              <Badge className="bg-amber-100 text-amber-700 border-amber-200">
                {pendingProposals.length}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <ul className="space-y-3">
              {pendingProposals.map((proposal) => (
                <li key={proposal.id}>
                  <button
                    type="button"
                    onClick={() =>
                      setSelectedProposalJobOfferId(proposal.jobOfferId)
                    }
                    className="block w-full text-left border border-slate-200 rounded-xl p-4 hover:border-amber-300 transition cursor-pointer"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        {(proposal.providerFirstName ||
                          proposal.providerLastName) && (
                          <p className="text-sm font-medium text-slate-700 truncate">
                            {[
                              proposal.providerFirstName,
                              proposal.providerLastName,
                            ]
                              .filter(Boolean)
                              .join(" ")}
                          </p>
                        )}
                        <p className="font-semibold text-slate-900 truncate">
                          {proposal.facilityName || "Unknown Facility"}
                        </p>
                        {proposal.jobOfferTitle && (
                          <p className="text-sm text-slate-600 truncate">
                            {proposal.jobOfferTitle}
                          </p>
                        )}
                        {proposal.initiatedNote && (
                          <p className="text-sm text-slate-500 mt-1 line-clamp-2">
                            {proposal.initiatedNote}
                          </p>
                        )}
                        <ProposalScheduleChanges
                          schedulesToAdd={proposal.schedulesToAdd}
                          shiftsToCancel={proposal.shiftsToCancel}
                        />
                      </div>
                      <p className="text-xs text-slate-400 shrink-0 mt-0.5">
                        {formatDistanceToNow(new Date(proposal.initiatedAt), {
                          addSuffix: true,
                        })}
                      </p>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {!hasBusinessUnit && (
        <Alert className="border-blue-200 bg-blue-50/80">
          <AlertTitle>Select a business unit</AlertTitle>
          <AlertDescription>
            Choose an organization business unit from the selector above to see
            live metrics and activity.
          </AlertDescription>
        </Alert>
      )}

      {statsLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {[...Array(4).keys()].map((key) => (
            <Card key={key} className="border border-slate-200 shadow-sm">
              <CardContent className="p-5 space-y-4">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-10 w-16" />
                <Skeleton className="h-3 w-32" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {stats.map(({ label, value, description, icon: Icon }) => (
            <Card
              key={label}
              className="border border-slate-200 shadow-sm rounded-2xl"
            >
              <CardContent className="p-5 flex flex-col gap-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
                      {label}
                    </p>
                    <p className="mt-2 text-3xl font-semibold text-slate-900">
                      {value}
                    </p>
                  </div>
                  <span className="p-3 rounded-full bg-blue-50 text-blue-600">
                    <Icon className="h-5 w-5" />
                  </span>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed">
                  {description}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <div className="grid gap-6 xl:grid-cols-[2fr_1fr]">
        <Card className="min-w-0 border border-slate-200 shadow-sm rounded-2xl">
          <CardHeader className="space-y-3">
            <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
              <div className="space-y-2">
                <CardTitle className="text-xl font-semibold text-slate-900">
                  Active Job Posts
                </CardTitle>
                <CardDescription>
                  Track performance and quickly follow up with interested
                  providers.
                </CardDescription>
              </div>
              <Button
                asChild
                variant="ghost"
                className="self-start text-blue-600 hover:text-blue-700"
              >
                <Link to={orgPath("/jobs/post")}>
                  Manage posts
                  <ArrowUpRight className="ml-1 h-4 w-4" />
                </Link>
              </Button>
            </div>
            <div className="flex flex-wrap gap-2 text-xs">
              <Badge
                variant="secondary"
                className="bg-emerald-50 text-emerald-700"
              >
                Published {activeJobPostCount}
              </Badge>
              <Badge variant="secondary" className="bg-amber-50 text-amber-700">
                Pending review {pendingReviewCount}
              </Badge>
              <Badge
                variant="secondary"
                className="bg-slate-100 text-slate-700"
              >
                Drafts {draftJobCount}
              </Badge>
              <Badge variant="secondary" className="bg-blue-50 text-blue-700">
                Interested candidates {totalInterestedCandidates}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {jobPostsLoading ? (
              <div className="space-y-4">
                {[...Array(3).keys()].map((key) => (
                  <Skeleton key={key} className="h-24 w-full rounded-xl" />
                ))}
              </div>
            ) : jobPostsError ? (
              <Alert variant="destructive">
                <AlertTitle>Unable to load job posts</AlertTitle>
                <AlertDescription>
                  {jobPostsErrorData instanceof Error
                    ? jobPostsErrorData.message
                    : "An unexpected error occurred while loading job posts."}
                </AlertDescription>
              </Alert>
            ) : highlightedJobPosts.length > 0 ? (
              <ul className="space-y-4">
                {highlightedJobPosts.map((job) => {
                  const updatedAtIso = job.updatedAt || job.createdAt;
                  const updatedDate = updatedAtIso
                    ? parseISO(updatedAtIso)
                    : new Date();
                  const publishedAtIso = job.publishedAt;
                  const publishedLabel = publishedAtIso
                    ? `Published ${formatDistanceToNow(
                        parseISO(publishedAtIso),
                        {
                          addSuffix: true,
                        },
                      )}`
                    : "Not yet published";
                  const interestedCount = job.interestedCandidates?.length ?? 0;
                  const locationParts = [job.city, job.stateCode]
                    .filter(Boolean)
                    .join(", ");
                  const statusBadge = getJobStatusBadge(job);

                  return (
                    <li
                      key={job.id}
                      className="border border-slate-200 rounded-xl p-4 hover:border-blue-300 transition"
                    >
                      <div className="flex flex-col gap-3 md:flex-row md:justify-between md:items-start">
                        <div className="space-y-2">
                          <div className="flex items-start gap-2 flex-wrap">
                            <h3 className="text-lg font-semibold text-slate-900">
                              {job.title || "Untitled role"}
                            </h3>
                            <Badge
                              variant="outline"
                              className={statusBadge.className}
                            >
                              {statusBadge.label}
                            </Badge>
                          </div>
                          <div className="flex flex-wrap items-center gap-3 text-sm text-slate-600">
                            {job.facility?.name || job.facilityName ? (
                              <span className="inline-flex items-center gap-1">
                                <Building2 className="h-4 w-4" />
                                {job.facility?.name || job.facilityName}
                              </span>
                            ) : null}
                            {locationParts ? (
                              <span className="inline-flex items-center gap-1">
                                <MapPin className="h-4 w-4" />
                                {locationParts}
                              </span>
                            ) : null}
                            {job.remote ? (
                              <span className="inline-flex items-center gap-1">
                                <Sparkles className="h-4 w-4" />
                                Remote friendly
                              </span>
                            ) : null}
                            <span className="inline-flex items-center gap-1">
                              <Users className="h-4 w-4" />
                              {interestedCount} interested
                            </span>
                          </div>
                        </div>
                        <Button asChild variant="secondary" size="sm">
                          <Link to={orgPath(`/jobs/post/dashboard/${job.id}`)}>
                            View dashboard
                          </Link>
                        </Button>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-4 text-xs text-slate-500">
                        <span className="inline-flex items-center gap-1">
                          <Clock className="h-3.5 w-3.5" />
                          Updated{" "}
                          {formatDistanceToNow(updatedDate, {
                            addSuffix: true,
                          })}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <Calendar className="h-3.5 w-3.5" />
                          {publishedLabel}
                        </span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <EmptyState
                title="No job posts yet"
                description="Create your first job post to reach qualified providers."
                actionLabel="Create job post"
                actionTo={orgPath("/jobs/post/create")}
              />
            )}
          </CardContent>
        </Card>

        <div className="min-w-0 space-y-6">
          <Card className="border border-slate-200 shadow-sm rounded-2xl">
            <CardHeader className="space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <CardTitle className="text-xl font-semibold text-slate-900">
                    Job Offer Pipeline
                  </CardTitle>
                  <CardDescription>
                    Monitor outstanding offers and provider responses.
                  </CardDescription>
                </div>
                <Button
                  asChild
                  variant="ghost"
                  className="self-start text-blue-600 hover:text-blue-700"
                >
                  <Link to={orgPath("/jobs/offer")}>
                    Manage offers
                    <ArrowUpRight className="ml-1 h-4 w-4" />
                  </Link>
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 text-xs">
                <Badge variant="secondary" className="bg-blue-50 text-blue-700">
                  Awaiting {offerStatusCounts.awaiting}
                </Badge>
                <Badge
                  variant="secondary"
                  className="bg-emerald-50 text-emerald-700"
                >
                  Accepted {offerStatusCounts.accepted}
                </Badge>
                <Badge variant="secondary" className="bg-rose-50 text-rose-700">
                  Declined {offerStatusCounts.declined}
                </Badge>
                <Badge
                  variant="secondary"
                  className="bg-slate-100 text-slate-700"
                >
                  Canceled {offerStatusCounts.canceled}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              {jobOffersLoading ? (
                <div className="space-y-4">
                  {[...Array(3).keys()].map((key) => (
                    <Skeleton key={key} className="h-20 w-full rounded-xl" />
                  ))}
                </div>
              ) : jobOffersError ? (
                <Alert variant="destructive">
                  <AlertTitle>Unable to load job offers</AlertTitle>
                  <AlertDescription>
                    {jobOffersErrorData instanceof Error
                      ? jobOffersErrorData.message
                      : "An unexpected error occurred while loading job offers."}
                  </AlertDescription>
                </Alert>
              ) : highlightedOffers.length > 0 ? (
                <ul className="space-y-4">
                  {highlightedOffers.map((offer) => {
                    const statusBadge = getOfferStatusBadge(offer);
                    const facilityName = offer.facility?.name ?? "";
                    const locationParts = [
                      offer.facility?.city,
                      offer.facility?.state,
                    ]
                      .filter(Boolean)
                      .join(", ");
                    const firstShift = offer.shifts?.[0];
                    const hasDates = Boolean(firstShift?.startsAt);

                    return (
                      <li
                        key={offer.id}
                        className="border border-slate-200 rounded-xl p-4 hover:border-blue-300 transition"
                      >
                        <div className="flex flex-col gap-3">
                          <div className="flex justify-between items-start gap-3">
                            <div className="space-y-2">
                              <div className="flex items-start gap-2 flex-wrap">
                                <h3 className="text-lg font-semibold text-slate-900">
                                  {offer.title || "Untitled offer"}
                                </h3>
                                <Badge
                                  variant="outline"
                                  className={statusBadge.className}
                                >
                                  {statusBadge.label}
                                </Badge>
                              </div>
                              <div className="flex flex-wrap gap-3 text-sm text-slate-600">
                                {facilityName ? (
                                  <span className="inline-flex items-center gap-1">
                                    <Building2 className="h-4 w-4" />
                                    {facilityName}
                                  </span>
                                ) : null}
                                {locationParts ? (
                                  <span className="inline-flex items-center gap-1">
                                    <MapPin className="h-4 w-4" />
                                    {locationParts}
                                  </span>
                                ) : null}
                                {hasDates ? (
                                  <span className="inline-flex items-center gap-1">
                                    <Calendar className="h-4 w-4" />
                                    {formatOfferShiftRange(firstShift)}
                                  </span>
                                ) : null}
                              </div>
                            </div>
                            <Button asChild variant="secondary" size="sm">
                              <Link to={orgPath("/jobs/offer")}>Open</Link>
                            </Button>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <EmptyState
                  title="No active job offers"
                  description="Send offers to candidates as you progress through hiring."
                  actionLabel="Create job offer"
                  actionTo={orgPath("/jobs/offer/create")}
                />
              )}
            </CardContent>
          </Card>

          <Card className="border border-slate-200 shadow-sm rounded-2xl">
            <CardHeader className="space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <CardTitle className="text-xl font-semibold text-slate-900">
                    Providers Shared With You
                  </CardTitle>
                  <CardDescription>
                    Quickly access providers your team is reviewing.
                  </CardDescription>
                </div>
                <Button
                  asChild
                  variant="ghost"
                  className="self-start text-blue-600 hover:text-blue-700"
                >
                  <Link to={orgPath("/providers/all")}>
                    View all
                    <ArrowUpRight className="ml-1 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {providersLoading ? (
                <div className="space-y-4">
                  {[...Array(5).keys()].map((key) => (
                    <Skeleton key={key} className="h-16 w-full rounded-xl" />
                  ))}
                </div>
              ) : providersError ? (
                <Alert variant="destructive">
                  <AlertTitle>Unable to load providers</AlertTitle>
                  <AlertDescription>
                    {providersErrorData instanceof Error
                      ? providersErrorData.message
                      : "An unexpected error occurred while loading providers."}
                  </AlertDescription>
                </Alert>
              ) : highlightedProviders.length > 0 ? (
                <ul className="space-y-4">
                  {highlightedProviders.map((provider) => {
                    const name =
                      `${provider.firstName ?? ""} ${provider.lastName ?? ""}`.trim() ||
                      "Unknown provider";
                    const initials =
                      `${provider.firstName?.[0] ?? ""}${provider.lastName?.[0] ?? ""}`.toUpperCase() ||
                      "PR";

                    return (
                      <li
                        key={provider.id}
                        className="flex items-start gap-3 border border-slate-200 rounded-xl p-3"
                      >
                        <Avatar className="h-10 w-10">
                          <AvatarFallback>{initials}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1 space-y-1">
                          <div className="flex items-center justify-between gap-2">
                            <p className="font-medium text-slate-900">{name}</p>
                            <Link
                              to="/org/bu/$organizationBusinessUnitId/provider-profile/$userId"
                              params={{
                                organizationBusinessUnitId:
                                  activeBusinessUnitId ?? "",
                                userId: provider.id,
                              }}
                              className="text-sm font-medium text-blue-600 hover:text-blue-700"
                            >
                              View profile
                            </Link>
                          </div>
                          {provider.specialties?.length ? (
                            <div className="flex flex-wrap gap-1.5">
                              {provider.specialties
                                .slice(0, 2)
                                .map((specialty) => (
                                  <Badge
                                    key={specialty.id}
                                    variant="secondary"
                                    className="bg-blue-50 text-blue-700"
                                  >
                                    {specialty.name}
                                  </Badge>
                                ))}
                              {provider.specialties.length > 2 && (
                                <Badge
                                  variant="outline"
                                  className="text-slate-600"
                                >
                                  +{provider.specialties.length - 2} more
                                </Badge>
                              )}
                            </div>
                          ) : (
                            <p className="text-sm text-slate-500">
                              Specialty details not provided yet.
                            </p>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <EmptyState
                  title="No shared providers yet"
                  description="Ask your recruiting team to share provider profiles once they are ready for review."
                  actionLabel="Browse providers"
                  actionTo={orgPath("/providers/all")}
                />
              )}
            </CardContent>
          </Card>

          {canViewCredentials && (
            <Card className="border border-slate-200 shadow-sm rounded-2xl">
              <CardHeader className="space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <CardTitle className="text-xl font-semibold text-slate-900">
                      Credential Compliance
                    </CardTitle>
                    <CardDescription>
                      Credential status for hired professionals
                    </CardDescription>
                  </div>
                  <Button
                    asChild
                    variant="ghost"
                    className="self-start text-blue-600 hover:text-blue-700"
                  >
                    <Link
                      to="/org/bu/$organizationBusinessUnitId/providers/credentials"
                      params={{
                        organizationBusinessUnitId: activeBusinessUnitId ?? "",
                      }}
                      search={{ employment: "hired" }}
                    >
                      View dashboard
                      <ArrowUpRight className="ml-1 h-4 w-4" />
                    </Link>
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {credentialStatsLoading ? (
                  <div className="space-y-3">
                    <Skeleton className="h-10 w-24" />
                    {[...Array(4).keys()].map((key) => (
                      <Skeleton key={key} className="h-8 w-full rounded-lg" />
                    ))}
                  </div>
                ) : credentialStatsError ? (
                  <Alert variant="destructive">
                    <AlertTitle>Unable to load credential stats</AlertTitle>
                    <AlertDescription>
                      {credentialStatsErrorData instanceof Error
                        ? credentialStatsErrorData.message
                        : "An unexpected error occurred while loading credential stats."}
                    </AlertDescription>
                  </Alert>
                ) : credentialStats ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <span className="p-2 rounded-full bg-slate-50 text-slate-600">
                        <ShieldCheck className="h-5 w-5" />
                      </span>
                      <div>
                        <p className="text-2xl font-semibold text-slate-900 tabular-nums">
                          {credentialStats.total}
                        </p>
                        <p className="text-xs text-slate-500">
                          Hired professionals tracked
                        </p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between rounded-lg border border-emerald-100 bg-emerald-50/60 px-3 py-2">
                        <span className="text-xs font-medium text-emerald-700">
                          Up to date
                        </span>
                        <span className="text-sm font-semibold text-emerald-700 tabular-nums">
                          {credentialStats.current}
                        </span>
                      </div>
                      <div className="flex items-center justify-between rounded-lg border border-amber-100 bg-amber-50/60 px-3 py-2">
                        <span className="text-xs font-medium text-amber-700">
                          Expiring soon (30 days)
                        </span>
                        <span className="text-sm font-semibold text-amber-700 tabular-nums">
                          {credentialStats.expiringSoon}
                        </span>
                      </div>
                      <div className="flex items-center justify-between rounded-lg border border-red-100 bg-red-50/60 px-3 py-2">
                        <span className="text-xs font-medium text-red-700">
                          Expired
                        </span>
                        <span className="text-sm font-semibold text-red-700 tabular-nums">
                          {credentialStats.expired}
                        </span>
                      </div>
                      <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50/60 px-3 py-2">
                        <span className="text-xs font-medium text-slate-600">
                          Missing required
                        </span>
                        <span className="text-sm font-semibold text-slate-600 tabular-nums">
                          {credentialStats.missing}
                        </span>
                      </div>
                    </div>
                  </div>
                ) : null}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <JobOfferPreviewDialog
        jobOffer={selectedProposalJobOffer ?? null}
        open={!!selectedProposalJobOfferId}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedProposalJobOfferId(null);
          }
        }}
        user={user ? { is_admin: user.is_admin ?? false } : null}
      />
    </div>
  );
}

function getJobStatusBadge(job: {
  open: boolean | null;
  publishedAt: string | null;
  reviewedAt: string | null;
}) {
  if (!job.open) {
    return {
      label: "Closed",
      className: "bg-rose-50 text-rose-700 border-rose-200",
    };
  }
  if (job.publishedAt && !job.reviewedAt) {
    return {
      label: "Pending review",
      className: "bg-amber-50 text-amber-700 border-amber-200",
    };
  }
  if (job.publishedAt) {
    return {
      label: "Published",
      className: "bg-emerald-50 text-emerald-700 border-emerald-200",
    };
  }
  return {
    label: "Draft",
    className: "bg-slate-100 text-slate-700 border-slate-200",
  };
}

function getOfferStatusBadge(offer: {
  canceledByOrganizationBusinessUnitAt: string | null;
  decisions?: {
    providerDecision: boolean | null;
    providerCanceledAt: string | null;
  }[];
}) {
  if (offer.canceledByOrganizationBusinessUnitAt) {
    return {
      label: "Canceled",
      className: "bg-slate-100 text-slate-700 border-slate-200",
    };
  }

  const decisions = offer.decisions ?? [];
  if (decisions.some((decision) => decision?.providerDecision === true)) {
    return {
      label: "Accepted",
      className: "bg-emerald-50 text-emerald-700 border-emerald-200",
    };
  }
  if (
    decisions.some(
      (decision) =>
        decision?.providerDecision === false || decision?.providerCanceledAt,
    )
  ) {
    return {
      label: "Declined",
      className: "bg-rose-50 text-rose-700 border-rose-200",
    };
  }
  return {
    label: "Awaiting response",
    className: "bg-blue-50 text-blue-700 border-blue-200",
  };
}

function formatOfferShiftRange(shift?: {
  startsAt?: Date | string | null;
  endsAt?: Date | string | null;
}) {
  if (!shift?.startsAt) {
    return "Dates pending";
  }
  const start =
    typeof shift.startsAt === "string"
      ? parseISO(shift.startsAt)
      : shift.startsAt;
  if (!shift.endsAt) {
    return `Starts ${format(start, "MMM d, yyyy")}`;
  }
  const end =
    typeof shift.endsAt === "string" ? parseISO(shift.endsAt) : shift.endsAt;
  return `${format(start, "MMM d, yyyy")} – ${format(end, "MMM d, yyyy")}`;
}

function EmptyState({
  title,
  description,
  actionLabel,
  actionTo,
}: {
  title: string;
  description: string;
  actionLabel: string;
  actionTo: string;
}) {
  return (
    <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/70 p-8 text-center space-y-4">
      <div className="space-y-2">
        <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
        <p className="text-sm text-slate-600 max-w-md mx-auto">{description}</p>
      </div>
      <Button
        asChild
        className="h-auto min-h-12 max-w-full whitespace-normal text-center font-semibold"
      >
        <Link to={actionTo}>{actionLabel}</Link>
      </Button>
    </div>
  );
}
